import React, { useEffect, useState } from 'react'
import { useStoreState } from 'easy-peasy'
import styled from 'styled-components'
import { Spinner } from 'reactstrap'
import components from '../components/common'
import media from '../utils/media'
import Image from '../components/common/Image'
import Text, { useTranslations } from '../utils/Text'
import Paragraph from '../components/common/Paragraph'
import { getParam } from '../helpers'
import { postConfirmEmail } from '../services/email'
import CheckCircleGreen from '../components/icons/CheckCircleGreen'
import CopyToClipboard from '../components/common/CopyToClipboard'

const Container = styled.div`
  text-align: center;
  width: 100%;
  background-color: #ffffff;
  margin-right: auto;
  margin-left: auto;
  max-width: 726px;

  ${media.tabletS`
    margin: 0 18px;
  `}

  .form-component {
    margin: auto;
    padding-top: 50px;

    ${media.tabletS`
      padding-top: 20px;
  `}
  }
  .form-subtitle {
    padding: 15px 20px 0;
    font-weight: bold;
  }
  .form-title {
    a {
      color: #414141 !important;
      text-decoration: underline !important;
    }
  }
  #submit {
    width: 100%;
    letter-spacing: 1.85px;
    line-height: normal;
  }
`
const Content = styled.div`
  text-align: center;
  position: relative;
  ${media.tabletS`
    display: flex;
    flex-wrap: wrap;
  `}
`
const WrapperValid = styled.div`
  .container-wrapper {
    height: 270px;
  }
  ${media.tabletS`
  .container-wrapper {
    height: 380px;
  }
  `}
`
const Wrapper = styled.div`
  .container-wrapper {
    height: 270px;
  }
  ${media.tabletS`
  .container-wrapper {
    height: 110px;
  }
  `}
`
const IconWrapper = styled.div`
  margin: 25px auto 53px;
  ${media.tabletS`
    margin: 20px auto 37px;
  `}
`
const ScanQR = styled.div`
  font-family: Muli;
  font-weight: 600;
  font-size: ${props => (props.fontSize ? props.fontSize : 16)}px;
  line-height: ${props =>
    props.lineHeight ? props.lineHeight : 1.5};
  padding-bottom: 16px;
`
const BtnWrapper = styled.div`
  text-align: center;
  margin-bottom: 76px;
  ${media.tabletS`
    margin-bottom: 58px;
  `}
`
const Button = styled.a`
  height: 50px;
  width: ${props => (props.btnWidth ? `${props.btnWidth}` : '262px')};
  background-color: ${props => props.btnBg} !important;
  color: ${props => props.btnColor} !important;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  letter-spacing: 1.85px;
  line-height: 21px;
  white-space: nowrap;
  vertical-align: middle;
  padding: 15px;
  border: none;
  text-align: center;
  touch-action: manipulation;
  cursor: pointer;
  &:hover {
    text-decoration: none !important;
  }
  &:focus {
    outline: none !important;
    box-shadow: none !important;
  }
  &:active {
    opacity: 0.7;
  }
  ${media.mobileL`
    width: ${props => props.btnWidthMobile && props.btnWidthMobile};
  `}
`
const SpinnerWrapper = styled.div`
  display: grid;
  place-items: center;
  align-items: center;
  height: 55vh;
`

export default function Landing({ children }) {
  const [
    landing,
    locale,
    { consumed, state, sameDevice, productName, scanParamQ, product },
    scanPoints,
    validated,
    generalError,
  ] = useStoreState(state => [
    state.landing.landing,
    state.locale.locale,
    state.scanData.data,
    state.scanPoints,
    state.form.validated,
    state.form.generalError,
  ])

  const [loading, setLoading] = useState(false)
  const [emailValidated, setEmailValidated] = useState(false)
  const srcIconCheck = useTranslations(landing.icon.srcCheck, {})
  const LIC_URL = `${process.env.REACT_APP_LIC_URL}login`

  // TODO MOVE TO OTHER FILE? FUTURE PROBLEMS
  const STATES = {
    ERROR: 'ERROR',
    VALID: 'VALID',
    INACTIVE: 'INACTIVE',
    BLACKLIST: 'BLACK_LIST',
  }

  const PRODUCT = {
    FLAVOC: product === '015' || product === '016',
    FLAVOC10: product === '015', // 10 days
    FLAVOC30: product === '016', // 30 days
  }

  const LOCALE = {
    Allowed: locale !== 'en',
    notAllowed: locale === 'en',
    es: locale === 'es-es',
    country: locale === 'notAllowed', // used for IT until: https://jira.isdin.com/browse/LIC-493
    extended: [
      'es-es',
      'it-it',
      'es-cl',
      'es-ar',
      'es-pe',
      'es-co',
      'es-mx',
      'pt-br',
      'de-de',
      'fr-fr',
      'nl-be',
      'pt-pt',
    ].includes(locale),
  }

  const LANDING = {
    valid: state === STATES.VALID && !consumed && !generalError, // 0: landing valid
    sameDevice:
      state === STATES.VALID &&
      consumed &&
      sameDevice &&
      !generalError, // 1: QR Consumed sameDevice
    otherDevice:
      state === STATES.VALID &&
      consumed &&
      !sameDevice &&
      !generalError, // 2: QR Consumed otherDevice
    blackList: state === STATES.BLACKLIST, // 3: QR Black List
    error: state === STATES.ERROR || generalError, // 4: Error
    validated: emailValidated, // 5: Email Validated (redirect mail)
    inactive: state === STATES.INACTIVE, // 6: QR inactive
  }
  const CLIPBOARD = {
    qrparam:
      LANDING.blackList ||
      LANDING.inactive ||
      LANDING.otherDevice ||
      LANDING.sameDevice, // copyToClipboard
  }

  const F10 = useTranslations(landing.valid.textFlavoC10, {})
  const F30 = useTranslations(landing.valid.textFlavoC30, {})

  const paragraphFlavoC = `${useTranslations(
    landing.valid.textProduct,
    {},
  )} **${productName}**${PRODUCT.FLAVOC && LOCALE.es ? '' : '.'} 
  ${PRODUCT.FLAVOC10 ? ` ${F10}` : '&Tab;'} 
  ${PRODUCT.FLAVOC30 ? `${F30}` : '&Tab;'}
   `

  const textPoints = `${useTranslations(
    landing.valid.textPoints1,
    {},
  )} **${scanPoints}** ${useTranslations(
    landing.valid.textPoints2,
    {},
  )}
   `

  const checkUrlParams = () => {
    const leadId = getParam('leadId')
    const token = getParam('token')

    if (leadId && token) {
      setLoading(true)
      postConfirmEmail().then(response => {
        if (response.success) {
          setLoading(false)
          setEmailValidated(true)
        }
      })
    }
  }

  useEffect(() => {
    checkUrlParams()
  }, [])

  return (
    <>
      <>
        {loading ? (
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        ) : (
          <>
            {LOCALE.Allowed && !LOCALE.country && (
              <div locale={locale}>
                {/* 0: landing valid */}
                {LANDING.valid && (
                  <>
                    {landing.scanOK &&
                      !PRODUCT.FLAVOC &&
                      landing.scanOK.map(item => {
                        const TypeComponent = components[item.type]
                        return (
                          <WrapperValid
                            key={`Components-${item.type}`}
                          >
                            <TypeComponent
                              key={`scanOK-${item.type}`}
                              config={item.config}
                            />
                          </WrapperValid>
                        )
                      })}
                    {landing.flavoC &&
                      PRODUCT.FLAVOC &&
                      landing.flavoC.map(item => {
                        const TypeComponent = components[item.type]
                        return (
                          <WrapperValid
                            key={`product-flavoC-${item.type}`}
                          >
                            <TypeComponent
                              key={`flavoC-${item.type}`}
                              config={item.config}
                            />
                          </WrapperValid>
                        )
                      })}
                    <Content>
                      <Container locale={locale}>
                        {landing.title && (
                          <Paragraph
                            paragraph={
                              landing.valid &&
                              landing.valid.validTitle
                            }
                            marginB={landing.title.marginBText}
                            padding={landing.title.paddingTitle}
                            paddingMobile={
                              landing.title.paddingMobileTitle
                            }
                            fontSize={landing.title.titleSize}
                            lineHeight={landing.title.lineHeight}
                            titleColor={landing.title.titleColor}
                            fontWeight={landing.title.fontWeight}
                          />
                        )}
                        {landing.icon && (
                          <IconWrapper>
                            <Image
                              src={srcIconCheck}
                              alt={landing.icon.alt}
                              maxWidth={landing.icon.maxWidth}
                              minWidth={landing.icon.minWidth}
                            />
                          </IconWrapper>
                        )}
                        {validated === null && landing.title && (
                          <>
                            {!LOCALE.es && (
                              <Paragraph
                                paragraph={landing.valid.textProduct}
                                fontFamily={landing.icon.fontFamily}
                                marginB={landing.title.marginBText}
                                fontSize={landing.title.subtitleSize}
                                lineHeight={
                                  landing.title.subtitleLineHeight
                                }
                                padding={landing.title.padding}
                                fontWeight={landing.title.fontWeight}
                              />
                            )}

                            {LOCALE.es ? (
                              <Paragraph
                                content={paragraphFlavoC + textPoints}
                                fontFamily={landing.icon.fontFamily}
                                marginB={landing.title.marginBText}
                                fontSize={landing.title.subtitleSize}
                                lineHeight={
                                  landing.title.subtitleLineHeight
                                }
                                classN="mb-0 px-5"
                                padding={landing.title.padding}
                                fontWeight={landing.title.fontWeight}
                              />
                            ) : (
                              <Paragraph
                                content={textPoints}
                                fontFamily={landing.icon.fontFamily}
                                marginB={landing.title.marginBText}
                                fontSize={landing.title.subtitleSize}
                                lineHeight={
                                  landing.title.subtitleLineHeight
                                }
                                padding={landing.title.padding}
                                fontWeight={landing.title.fontWeight}
                              />
                            )}
                          </>
                        )}
                        <div id="form">{children}</div>
                      </Container>
                    </Content>
                    {/*  lastComponents  */}
                    {landing.lastComponents &&
                      !PRODUCT.FLAVOC &&
                      landing.lastComponents.map((item, i) => {
                        const TypeComponent = components[item.type]
                        return (
                          <TypeComponent
                            // eslint-disable-next-line react/no-array-index-key
                            key={`lastComponents-${item.type}-${i}`}
                            config={item.config}
                          />
                        )
                      })}

                    {/*  lastComponents  */}
                    {landing.lastComponentsFlavoC &&
                      PRODUCT.FLAVOC &&
                      landing.lastComponentsFlavoC.map((item, i) => {
                        const TypeComponent = components[item.type]
                        return (
                          <TypeComponent
                            // eslint-disable-next-line react/no-array-index-key
                            key={`lastComponentsFlavoC-${item.type}-${i}`}
                            config={item.config}
                          />
                        )
                      })}
                  </>
                )}
                {/* 1: QR Consumed sameDevice */}
                {LANDING.sameDevice && (
                  <>
                    {landing.firstComponents &&
                      landing.firstComponents.map(item => {
                        const TypeComponent = components[item.type]
                        return (
                          <Wrapper key={`Components-${item.type}`}>
                            <TypeComponent
                              key={`firstComponents-${item.type}`}
                              config={item.config}
                            />
                          </Wrapper>
                        )
                      })}
                    <Content>
                      <Container locale={locale}>
                        {landing.title && (
                          <>
                            <Paragraph
                              paragraph={
                                landing.sameDevice &&
                                landing.sameDevice.sameDeviceTitle
                              }
                              marginB={landing.title.marginBTitle}
                              fontSize={landing.title.titleSize}
                              lineHeight={landing.title.lineHeight}
                              padding={landing.title.paddingTitle}
                              paddingMobile={
                                landing.title.paddingMobileTitle
                              }
                              classN="form-title"
                            />
                            <Paragraph
                              paragraph={
                                landing.sameDevice &&
                                landing.sameDevice.sameDeviceParagraph
                              }
                              fontFamily={landing.icon.fontFamily}
                              marginB={landing.title.marginBTitle}
                              fontSize={landing.title.subtitleSize}
                              lineHeight={
                                landing.title.subtitlelineHeight
                              }
                              padding={landing.title.subtitlePadding}
                              classN="form-title"
                            />
                            <Paragraph
                              paragraph={
                                landing.sameDevice &&
                                landing.sameDevice.sameDeviceFooter
                              }
                              fontFamily={landing.icon.fontFamily}
                              marginB={landing.title.marginBTitle}
                              fontSize={landing.title.footerReadSize}
                              lineHeight={
                                landing.title.subtitlelineHeight
                              }
                              padding={landing.title.padding}
                              classN="form-title"
                            />
                          </>
                        )}
                      </Container>
                    </Content>
                  </>
                )}
                {/* 2: QR Consumed otherDevice */}
                {LANDING.otherDevice && (
                  <>
                    {landing.firstComponents &&
                      landing.firstComponents.map(item => {
                        const TypeComponent = components[item.type]
                        return (
                          <Wrapper key={`Components-${item.type}`}>
                            <TypeComponent
                              key={`firstComponents-${item.type}`}
                              config={item.config}
                            />
                          </Wrapper>
                        )
                      })}
                    <Content>
                      <Container locale={locale}>
                        {landing.title && (
                          <>
                            <Paragraph
                              paragraph={
                                landing.otherDevice &&
                                landing.otherDevice.otherDeviceTitle
                              }
                              marginB={landing.title.marginBTitle}
                              fontSize={landing.title.titleSize}
                              lineHeight={landing.title.lineHeight}
                              padding={landing.title.paddingTitle}
                              paddingMobile={
                                landing.title.paddingMobileTitle
                              }
                              classN="form-title"
                            />
                            <Paragraph
                              paragraph={
                                landing.otherDevice &&
                                landing.otherDevice
                                  .otherDeviceParagraph
                              }
                              fontFamily={landing.icon.fontFamily}
                              marginB={landing.title.marginBTitle}
                              fontSize={landing.title.subtitleSize}
                              lineHeight={
                                landing.title.subtitlelineHeight
                              }
                              padding={landing.title.subtitlePadding}
                              classN="form-title"
                            />
                            <Paragraph
                              paragraph={
                                landing.otherDevice &&
                                landing.otherDevice.otherDeviceFooter
                              }
                              fontFamily={landing.icon.fontFamily}
                              marginB={landing.title.marginBTitle}
                              fontSize={landing.title.footerReadSize}
                              lineHeight={
                                landing.title.subtitlelineHeight
                              }
                              padding={landing.title.padding}
                              classN="form-title"
                            />
                          </>
                        )}
                      </Container>
                    </Content>
                  </>
                )}
                {/* 3: QR Black List */}
                {LANDING.blackList && (
                  <>
                    {landing.firstComponents &&
                      landing.firstComponents.map(item => {
                        const TypeComponent = components[item.type]
                        return (
                          <Wrapper key={`Components-${item.type}`}>
                            <TypeComponent
                              key={`firstComponents-${item.type}`}
                              config={item.config}
                            />
                          </Wrapper>
                        )
                      })}
                    <Content>
                      <Container locale={locale}>
                        {landing.title && (
                          <>
                            <Paragraph
                              paragraph={
                                landing.blackList &&
                                landing.blackList.blackListTitle
                              }
                              marginB={
                                landing.blackList
                                  .blackListMarginBTitle
                              }
                              fontSize={landing.title.titleSize}
                              lineHeight={landing.title.lineHeight}
                              padding={landing.title.paddingTitle}
                              paddingMobile={
                                landing.title.paddingMobileTitle
                              }
                              classN="form-title"
                            />
                            <Paragraph
                              paragraph={
                                landing.blackList &&
                                landing.blackList.blackListParagraph
                              }
                              fontFamily={landing.icon.fontFamily}
                              marginB={landing.title.marginBTitle}
                              fontSize={landing.title.subtitleSize}
                              lineHeight={
                                landing.title.subtitlelineHeight
                              }
                              padding={landing.title.subtitlePadding}
                              classN="form-title"
                            />
                          </>
                        )}
                      </Container>
                    </Content>
                  </>
                )}
                {/* 4: Error */}
                {LANDING.error && !LANDING.validated && (
                  <>
                    {landing.firstComponents &&
                      landing.firstComponents.map(item => {
                        const TypeComponent = components[item.type]
                        return (
                          <Wrapper key={`Components-${item.type}`}>
                            <TypeComponent
                              key={`firstComponents-${item.type}`}
                              config={item.config}
                            />
                          </Wrapper>
                        )
                      })}
                    <Content>
                      <Container locale={locale}>
                        {landing.title && (
                          <>
                            <Paragraph
                              paragraph={
                                landing.error &&
                                landing.error.errorTitle
                              }
                              marginB={landing.title.marginBTitle}
                              padding={landing.title.paddingTitle}
                              paddingMobile={
                                landing.title.paddingMobileTitle
                              }
                              fontSize={landing.title.titleSize}
                              lineHeight={landing.title.lineHeight}
                              classN="form-title"
                            />
                            <Paragraph
                              paragraph={
                                landing.error &&
                                landing.error.errorParagraph
                              }
                              fontFamily={landing.icon.fontFamily}
                              marginB={landing.title.marginBTitle}
                              fontSize={landing.title.subtitleSize}
                              lineHeight={
                                landing.title.subtitlelineHeight
                              }
                              padding={landing.title.padding}
                              classN="form-title"
                            />
                          </>
                        )}
                      </Container>
                    </Content>
                  </>
                )}
                {/* 5: Email Validated (redirect mail) */}
                {LANDING.validated && (
                  <>
                    {landing.firstComponents &&
                      landing.firstComponents.map(item => {
                        const TypeComponent = components[item.type]
                        return (
                          <Wrapper key={`Components-${item.type}`}>
                            <TypeComponent
                              key={`firstComponents-${item.type}`}
                              config={item.config}
                            />
                          </Wrapper>
                        )
                      })}
                    <Content>
                      <Container locale={locale}>
                        <div className="pt-3">
                          <CheckCircleGreen width="82" height="82" />
                        </div>
                        {landing.title && (
                          <>
                            <Paragraph
                              paragraph={
                                landing.consumed &&
                                landing.consumed.consumedTitle
                              }
                              marginB={landing.title.marginBTitle}
                              fontSize={landing.title.titleSize}
                              lineHeight={landing.title.lineHeight}
                              padding={landing.title.paddingConsumed}
                              paddingMobile={
                                landing.title.paddingMobileConsumed
                              }
                              classN="form-title"
                            />
                            <Paragraph
                              paragraph={
                                landing.consumed &&
                                landing.consumed.consumedParagraph
                              }
                              fontFamily={landing.icon.fontFamily}
                              marginB={landing.title.marginBSubtitle}
                              fontSize={landing.title.subtitleSize}
                              lineHeight={
                                landing.title.subtitlelineHeight
                              }
                              padding={landing.title.subtitlePadding}
                              classN="form-title"
                            />
                            {!LOCALE.extended && (
                              <Paragraph
                                paragraph={
                                  landing.consumed &&
                                  landing.consumed.consumedFooter
                                }
                                fontFamily={landing.icon.fontFamily}
                                marginB={landing.title.marginBTitle}
                                fontSize={landing.title.subtitleSize}
                                lineHeight={
                                  landing.title.subtitlelineHeight
                                }
                                padding={landing.title.padding}
                                classN="form-title"
                              />
                            )}
                          </>
                        )}
                      </Container>
                    </Content>

                    {/*  validContent  */}
                    {LOCALE.extended &&
                      landing.validContent &&
                      landing.validContent.map(item => {
                        const TypeComponent = components[item.type]
                        return (
                          <>
                            <TypeComponent
                              key={`validContent-${item.type}`}
                              config={item.config}
                            />
                            {LOCALE.es && (
                              <BtnWrapper>
                                <Button
                                  href={LIC_URL}
                                  btnBg={
                                    landing.validContentBtn.btnBg
                                  }
                                  btnColor={
                                    landing.validContentBtn.btnColor
                                  }
                                  btnWidth={
                                    landing.validContentBtn.btnWidth
                                  }
                                >
                                  <Text
                                    id={
                                      landing.validContentBtn.btnText
                                    }
                                  />
                                </Button>
                              </BtnWrapper>
                            )}
                          </>
                        )
                      })}
                  </>
                )}
                {/* 6: QR inactive */}
                {LANDING.inactive && (
                  <>
                    {landing.firstComponents &&
                      landing.firstComponents.map(item => {
                        const TypeComponent = components[item.type]
                        return (
                          <Wrapper key={`Components-${item.type}`}>
                            <TypeComponent
                              key={`firstComponents-${item.type}`}
                              config={item.config}
                            />
                          </Wrapper>
                        )
                      })}
                    <Content>
                      <Container locale={locale}>
                        {landing.title && (
                          <>
                            <Paragraph
                              paragraph={
                                landing.inactive &&
                                landing.inactive.inactiveTitle
                              }
                              marginB={landing.title.marginBTitle}
                              fontSize={landing.title.titleSize}
                              lineHeight={landing.title.lineHeight}
                              padding={landing.title.paddingTitle}
                              paddingMobile={
                                landing.title.paddingMobileTitle
                              }
                              classN="form-title"
                            />
                            <Paragraph
                              paragraph={
                                landing.inactive &&
                                landing.inactive.inactiveParagraph
                              }
                              fontFamily={landing.icon.fontFamily}
                              marginB={landing.title.marginBTitle}
                              fontSize={landing.title.subtitleSize}
                              lineHeight={
                                landing.title.subtitlelineHeight
                              }
                              padding={landing.title.subtitlePadding}
                              classN="form-title"
                            />
                          </>
                        )}
                      </Container>
                    </Content>
                  </>
                )}

                {/* Copy to clipboard Scan QR: Q */}
                {CLIPBOARD.qrparam && (
                  <Content>
                    <Container locale={locale}>
                      <Paragraph
                        paragraph={
                          landing.blackList &&
                          landing.blackList.blackListScan
                        }
                        fontFamily={landing.icon.fontFamily}
                        marginB={landing.title.marginBText}
                        fontSize={landing.title.footerSize}
                        lineHeight={landing.title.footerLineHeight}
                        padding={landing.title.subtitlePadding}
                        classN="form-title"
                      />

                      <ScanQR
                        fontSize={landing.title.footerSize}
                        lineHeight={landing.title.footerLineHeight}
                        className="d-inline-block"
                      >
                        {scanParamQ}
                      </ScanQR>
                      <CopyToClipboard
                        btnText={
                          landing.blackList &&
                          landing.blackList.blackListCta
                        }
                        btnMarginB={landing.title.marginBText}
                        clipboardText={scanParamQ}
                      />
                    </Container>
                  </Content>
                )}
              </div>
            )}

            {/* country with issues, used for italy until may 2021 */}
            {LOCALE.country && (
              <div locale={locale}>
                <>
                  {landing.firstComponents &&
                    landing.firstComponents.map(item => {
                      const TypeComponent = components[item.type]
                      return (
                        <Wrapper key={`Components-${item.type}`}>
                          <TypeComponent
                            key={`firstComponents-${item.type}`}
                            config={item.config}
                          />
                        </Wrapper>
                      )
                    })}
                  <Content>
                    <Container locale={locale}>
                      {landing.title && (
                        <>
                          <Paragraph
                            paragraph={
                              landing.italy &&
                              landing.italy.italyTitle
                            }
                            marginB={
                              landing.blackList.blackListMarginBTitle
                            }
                            fontSize={landing.title.titleSize}
                            lineHeight={landing.title.lineHeight}
                            padding={landing.title.paddingTitle}
                            paddingMobile={
                              landing.title.paddingMobileTitle
                            }
                            classN="form-title"
                          />
                          <Paragraph
                            paragraph={
                              landing.italy &&
                              landing.italy.italyParagraph
                            }
                            fontFamily={landing.icon.fontFamily}
                            marginB={landing.title.marginBTitle}
                            fontSize={landing.title.subtitleSize}
                            lineHeight={
                              landing.title.subtitlelineHeight
                            }
                            padding={landing.title.subtitlePadding}
                            classN="form-title"
                          />
                          <Paragraph
                            paragraph={
                              landing.italy &&
                              landing.italy.italyFooter
                            }
                            fontFamily={landing.icon.fontFamily}
                            marginB={landing.title.marginBTitle}
                            fontSize={landing.title.subtitleSize}
                            lineHeight={
                              landing.title.subtitlelineHeight
                            }
                            padding={landing.title.subtitlePadding}
                            classN="form-title"
                          />
                        </>
                      )}
                    </Container>
                  </Content>
                </>
              </div>
            )}
            {LOCALE.notAllowed && (
              <>
                {landing.firstComponents &&
                  landing.firstComponents.map(item => {
                    const TypeComponent = components[item.type]
                    return (
                      <Wrapper key={`Components-${item.type}`}>
                        <TypeComponent
                          key={`firstComponents-${item.type}`}
                          config={item.config}
                        />
                      </Wrapper>
                    )
                  })}
                <Content
                  className="form-component container"
                  locale={locale}
                >
                  <Container locale={locale}>
                    {landing.title && (
                      <>
                        <Paragraph
                          paragraph={
                            landing.notAllowed &&
                            landing.notAllowed.notAllowedTitle
                          }
                          marginB={landing.title.marginBTitle}
                          padding={landing.title.paddingTitle}
                          fontSize={landing.title.titleSize}
                          lineHeight={landing.title.lineHeight}
                          classN="form-title"
                        />
                        <Paragraph
                          paragraph={
                            landing.notAllowed &&
                            landing.notAllowed.notAllowedParagraph
                          }
                          fontFamily={landing.icon.fontFamily}
                          marginB={landing.title.marginBTitle}
                          fontSize={landing.title.subtitleSize}
                          lineHeight={
                            landing.title.subtitlelineHeight
                          }
                          padding={landing.title.subtitlePadding}
                          classN="form-title"
                        />
                      </>
                    )}
                  </Container>
                </Content>
              </>
            )}
          </>
        )}
      </>
      {landing.footerComponents &&
        landing.footerComponents.map(item => {
          const TypeComponent = components[item.type]
          return (
            <TypeComponent
              key={`footerComponents-${item.type}`}
              config={item.config}
            />
          )
        })}
    </>
  )
}
